/**
 * ОСВ по рахунку (ч.1036): розріз по субконто (один або два рівні) або по
 * валюті. Група рахунків → спершу субрахунки. Клік по рядку — картка
 * рахунку з відбором по цьому субконто.
 */
import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { accounts, reports, type AccountDetailRow } from '@/api/documents';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';
import LedgerFilters, { NoRegulatedAccess } from './LedgerFilters';
import { useLedgerParams } from './useLedgerParams';
import { balanceColumns, numCell, periodLabel, quantityColumns, TreeLabel, LABEL_W } from './ledgerColumns';
import { ledgerLinks, toggleCollapsed, visibleRows } from './ledgerModel';

type Row = AccountDetailRow & { _idx: string; _account: string; _kind?: 'total' };

const GROUPS: { value: string; label: string; currencyOnly?: boolean }[] = [
  { value: 'subconto1', label: 'по субконто' },
  { value: 'subconto1,subconto2', label: 'по субконто і договорах' },
  { value: 'currency', label: 'по валютах', currencyOnly: true },
];

export default function TrialBalanceAccountPage() {
  const st = useLedgerParams();
  const navigate = useNavigate();
  const account = st.get('account');
  const group = st.get('group') || 'subconto1';
  const withQty = st.get('qty') === '1';
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  const accountsQ = useQuery({
    queryKey: ['accounts-all'],
    queryFn: () => accounts.list({ only_postable: false, limit: 600 }),
    staleTime: 60 * 60 * 1000,
  });
  const q = useQuery({
    queryKey: ['ledger-tba', st.org, account, st.from, st.to, group, withQty],
    queryFn: () => reports.trialBalanceAccount({
      organization_id: st.org, account_code: account, date_from: st.from, date_to: st.to, group_by: group,
      with_quantity: withQty }),
    enabled: !!st.org && !!account,
  });
  const data = q.data;
  const period = { org: st.org, from: st.from, to: st.to };
  const withCurrency = group === 'currency';

  const rows = useMemo<Row[]>(() => {
    if (!data) return [];
    // Субконто під рядком-рахунком (група) належить цьому рахунку.
    let current = data.account_code;
    const all: Row[] = data.rows.map((r, i) => {
      if (r.kind === 'account' && r.account_code) current = r.account_code;
      return { ...r, _idx: String(i), _account: r.account_code ?? current };
    });
    const out = visibleRows(all, collapsed, (r) => r._idx);
    out.push({
      ...data.totals, kind: 'total', level: 0, label: 'Разом', account_code: null, subconto_type: null,
      subconto_id: null, currency_code: null, is_group: false, opening_cur: null, turnover_dt_cur: null,
      turnover_cr_cur: null, closing_cur: null, _idx: '_total', _account: data.account_code, _kind: 'total',
    });
    return out;
  }, [data, collapsed]);

  const cardLink = (r: Row) => ledgerLinks.accountCard({
    ...period, account: r._account,
    sc: r.kind === 'subconto' ? r.subconto_id : null,
    scName: r.kind === 'subconto' ? r.label : null,
  });

  const cols = useMemo<ColumnDef<Row>[]>(() => [
    {
      key: 'label', header: 'Субконто', width: LABEL_W,
      cell: (r) => r._kind ? <strong>{r.label}</strong> : (
        <TreeLabel level={r.level} isGroup={r.is_group} collapsed={collapsed.has(r._idx)}
          onToggle={() => setCollapsed((s) => toggleCollapsed(s, r._idx))}>
          <a className="ledger__link" title="Картка рахунку з відбором"
            onClick={(e) => { e.stopPropagation(); navigate(cardLink(r)); }}>{r.label}</a>
        </TreeLabel>
      ),
    },
    ...balanceColumns<Row>(),
    ...(withQty && data?.with_quantity ? quantityColumns<Row>() : []),
    ...(withCurrency ? [
      { key: 'opening_cur', header: <>Вал. сальдо<br />поч.</>, width: 110, align: 'right', cell: (r: Row) => numCell(r.opening_cur) },
      { key: 'turnover_dt_cur', header: <>Вал. оборот<br />Дт</>, width: 110, align: 'right', cell: (r: Row) => numCell(r.turnover_dt_cur) },
      { key: 'turnover_cr_cur', header: <>Вал. оборот<br />Кт</>, width: 110, align: 'right', cell: (r: Row) => numCell(r.turnover_cr_cur) },
      { key: 'closing_cur', header: <>Вал. сальдо<br />кін.</>, width: 110, align: 'right', cell: (r: Row) => numCell(r.closing_cur) },
    ] as ColumnDef<Row>[] : []),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [collapsed, withCurrency, withQty, data?.with_quantity, st.org, st.from, st.to]);

  if (!st.permsLoading && !st.hasPermission) {
    return <NoRegulatedAccess title="ОСВ по рахунку" total={st.totalAccessibleOrgs} />;
  }
  const accList = (accountsQ.data ?? []).slice().sort((a, b) => a.code.localeCompare(b.code));
  const title = data ? `ОСВ по рахунку ${data.account_code} ${data.account_name}` : `ОСВ по рахунку ${account || ''}`;

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>{title}{' '}<span className="ledger__subtitle">{periodLabel(st.from, st.to)}</span></h2>
        <Link to={ledgerLinks.trialBalance(period)} className="btn-link">← до ОСВ</Link>
      </div>

      <LedgerFilters state={st}
        printHref={account ? ledgerLinks.print('tba', { ...period, account, group, qty: withQty ? '1' : undefined }) : undefined}
        onExcel={account ? () => reports.ledgerXlsx('/reports/trial-balance/account', {
          organization_id: st.org, account_code: account, date_from: st.from, date_to: st.to, group_by: group,
          with_quantity: withQty }) : undefined}
        disabled={!data}>
        <div className="field" style={{ minWidth: 320 }}>
          <label>Рахунок</label>
          <select value={account} onChange={(e) => st.set({ account: e.target.value })}>
            <option value="">— оберіть —</option>
            {accList.map((a) => <option key={a.id} value={a.code}>{a.code} — {a.name}</option>)}
          </select>
        </div>
        <div className="field">
          <label>Розріз</label>
          <select value={group} onChange={(e) => st.set({ group: e.target.value === 'subconto1' ? null : e.target.value })}>
            {GROUPS.filter((g) => !g.currencyOnly || data?.use_currency || group === 'currency')
              .map((g) => <option key={g.value} value={g.value}>{g.label}</option>)}
          </select>
        </div>
        {(data?.use_quantity || withQty) && (
          <label className="ledger__check" title="Кількість по субконто (кількісний рахунок)">
            <input type="checkbox" checked={withQty}
              onChange={(e) => st.set({ qty: e.target.checked ? '1' : null })} />
            кількість
          </label>
        )}
      </LedgerFilters>

      {q.isError && <p className="ledger__warn">Не вдалося завантажити звіт: перевірте код рахунку.</p>}

      <DataTable<Row>
        rows={rows}
        columns={cols}
        rowKey={(r) => r._idx}
        loading={q.isLoading}
        emptyText={account ? 'За період рухів по рахунку немає.' : 'Оберіть рахунок.'}
        rowBold={(r) => r.is_group || !!r._kind}
        rowBackground={(r) => r._kind ? 'var(--bg-soft)' : undefined}
        onRowDoubleClick={(r) => { if (!r._kind) navigate(cardLink(r)); }}
        storageKey="ledger-trial-balance-account"
      />
      {data && <div className="ledger__totals"><span className="ledger__hint">клік по субконто — картка рахунку з відбором по ньому</span></div>}
    </div>
  );
}
